import { DeliveryInfo, DeliveryOptionsEnum, PaymentData, PaymentProvider } from '@as-react/common-types';
import type { MutationKey } from '@tanstack/react-query';
import {
  initSaferpayAlternative,
  paymentProviderInputRequest,
  PaymentProviderInputRequestParams,
  postClickAndCollectStoreId,
  postDelivery,
  postDeliveryCountry,
  postDeliveryOption,
  postOrderPaidByVoucher,
  postPayment,
  postPaymentOptionId,
  postPaymentProviderInput,
  postPaymentRequest,
  PostPaymentRequestParams,
  postPickupPoint,
  PostPickupPointParams,
  postRequestSaferpayToken,
  PostRequestSaferpayTokenParams,
  postSaferpayAlias,
  postSaferpayAlt,
  postSaferpayToken,
  PostSaferpayTokenParams,
  postShoplandingPayment,
  postSubmitOrder,
  PostSubmitOrderParams,
  postSubmitVoucher,
  PostVoucherSubmitParams,
} from './checkout.api';
import type { QueryApiOptions } from '../../types/types';

export const checkoutMutationKeys = {
  all: () => ['checkout'] as MutationKey,
  postDeliveryCountry: () => [...checkoutMutationKeys.all(), 'postDeliveryCountry'] as MutationKey,
  postDeliveryOption: () => [...checkoutMutationKeys.all(), 'postDeliveryOption'] as MutationKey,
  postDelivery: () => [...checkoutMutationKeys.all(), 'postDelivery'] as MutationKey,
  postPickupPoint: () => [...checkoutMutationKeys.all(), 'postPickupPoint'] as MutationKey,
  postPaymentOptionId: () => [...checkoutMutationKeys.all(), 'postPaymentOptionId'] as MutationKey,
  postClickAndCollectStoreId: () => [...checkoutMutationKeys.all(), 'postClickAndCollectStoreId'] as MutationKey,
  postSaferpayAlt: () => [...checkoutMutationKeys.all(), 'postSaferpayAlt'] as MutationKey,
  postSaferpayToken: () => [...checkoutMutationKeys.all(), 'postSaferpayToken'] as MutationKey,
  postOrderPaidByVoucher: () => [...checkoutMutationKeys.all(), 'postOrderPaidByVoucher'] as MutationKey,
  postPayment: () => [...checkoutMutationKeys.all(), 'postPayment'] as MutationKey,
  postPaymentProviderInput: () => [...checkoutMutationKeys.all(), 'postPaymentProviderInput'] as MutationKey,
  postSubmitOrder: () => [...checkoutMutationKeys.all(), 'postSubmitOrder'] as MutationKey,
  postSaferpayAlias: () => [...checkoutMutationKeys.all(), 'postSaferpayAlias'] as MutationKey,
  postSubmitVoucher: () => [...checkoutMutationKeys.all(), 'postSubmitVoucher'] as MutationKey,
  postShoplandingPayment: () => [...checkoutMutationKeys.all(), 'postShoplandingPayment'] as MutationKey,
  postInitSaferpayAlternative: () => [...checkoutMutationKeys.all(), 'postInitSaferpayAlternative'] as MutationKey,
  postRequestSaferpayToken: () => [...checkoutMutationKeys.all(), 'postRequestSaferpayToken'] as MutationKey,
  postPaymentRequest: () => [...checkoutMutationKeys.all(), 'postPaymentRequest'] as MutationKey,
  postPaymentProviderInputRequest: () => [...checkoutMutationKeys.all(), 'paymentProviderInputRequest'] as MutationKey,
};

export const checkoutMutations = (apiOptions: QueryApiOptions) => ({
  postDeliveryCountry: {
    mutationKey: checkoutMutationKeys.postDeliveryCountry(),
    mutationFn: (countryId: number) => postDeliveryCountry(apiOptions, countryId),
  },
  postDeliveryOption: {
    mutationKey: checkoutMutationKeys.postDeliveryOption(),
    mutationFn: (deliveryOptionId: DeliveryOptionsEnum) => postDeliveryOption(apiOptions, deliveryOptionId),
  },
  postDelivery: {
    mutationKey: checkoutMutationKeys.postDelivery(),
    mutationFn: (deliveryInfo: DeliveryInfo) => postDelivery(apiOptions, deliveryInfo),
  },
  postPickupPoint: {
    mutationKey: checkoutMutationKeys.postPickupPoint(),
    mutationFn: (params: PostPickupPointParams) => postPickupPoint(apiOptions, params),
  },
  postPaymentOptionId: {
    mutationKey: checkoutMutationKeys.postPaymentOptionId(),
    mutationFn: (optionId: number) => postPaymentOptionId(apiOptions, optionId),
  },
  postClickAndCollectStoreId: {
    mutationKey: checkoutMutationKeys.postClickAndCollectStoreId(),
    mutationFn: (storeId: number) => postClickAndCollectStoreId(apiOptions, storeId),
  },
  postSaferpayAlt: {
    mutationKey: checkoutMutationKeys.postSaferpayAlt(),
    mutationFn: (isMobileDevice: boolean) => postSaferpayAlt(apiOptions, isMobileDevice),
  },
  postSaferpayToken: {
    mutationKey: checkoutMutationKeys.postSaferpayToken(),
    mutationFn: (params: PostSaferpayTokenParams) => postSaferpayToken(apiOptions, params),
  },
  postOrderPaidByVoucher: {
    mutationKey: checkoutMutationKeys.postOrderPaidByVoucher(),
    mutationFn: () => postOrderPaidByVoucher(apiOptions),
  },
  postPayment: {
    mutationKey: checkoutMutationKeys.postPayment(),
    mutationFn: (paymentData: PaymentData) => postPayment(apiOptions, paymentData),
  },
  postPaymentProviderInput: {
    mutationKey: checkoutMutationKeys.postPaymentProviderInput(),
    mutationFn: (provider: PaymentProvider) => postPaymentProviderInput(apiOptions, provider),
  },
  postSubmitOrder: {
    mutationKey: checkoutMutationKeys.postSubmitOrder(),
    mutationFn: (params: PostSubmitOrderParams) => postSubmitOrder(apiOptions, params),
  },
  postSaferpayAlias: {
    mutationKey: checkoutMutationKeys.postSaferpayAlias(),
    mutationFn: (aliasId: string) => postSaferpayAlias(apiOptions, aliasId),
  },
  postSubmitVoucher: {
    mutationKey: checkoutMutationKeys.postSubmitVoucher(),
    mutationFn: (voucherData: PostVoucherSubmitParams) => postSubmitVoucher(apiOptions, voucherData),
  },
  postShoplandingPayment: {
    mutationKey: checkoutMutationKeys.postShoplandingPayment(),
    mutationFn: (shopNumber: string) => postShoplandingPayment(apiOptions, shopNumber),
  },
  postInitSaferpayAlternative: {
    mutationKey: checkoutMutationKeys.postInitSaferpayAlternative(),
    mutationFn: (paymentRequestId: string) => initSaferpayAlternative(apiOptions, paymentRequestId),
  },
  postRequestSaferpayToken: {
    mutationKey: checkoutMutationKeys.postRequestSaferpayToken(),
    mutationFn: (params: PostRequestSaferpayTokenParams) => postRequestSaferpayToken(apiOptions, params),
  },
  postPaymentRequest: {
    mutationKey: checkoutMutationKeys.postPaymentRequest(),
    mutationFn: (params: PostPaymentRequestParams) => postPaymentRequest(apiOptions, params),
  },
  postPaymentProviderInputRequest: {
    mutationKey: checkoutMutationKeys.postPaymentProviderInputRequest(),
    mutationFn: (params: PaymentProviderInputRequestParams) => paymentProviderInputRequest(apiOptions, params),
  },
});
